import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import type { InventoryItem } from '../../types';

interface InventoryDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: InventoryItem | null;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
}

export function InventoryDeleteDialog({
  open,
  onOpenChange,
  item,
  onConfirm,
  isLoading,
}: InventoryDeleteDialogProps) {
  const handleConfirm = async () => {
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to delete inventory item: ${message}`);
      console.error('Error deleting inventory item:', error);
    }
  };

  if (!item) return null;

  const hasStock = item.quantity_on_hand > 0;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Inventory Item</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete <strong>{item.sku_name || item.sku_code}</strong>? This action cannot be undone.
          </AlertDialogDescription>
          {hasStock && (
            <p className="text-sm text-orange-500 mt-2">
              This item has <strong>{item.quantity_on_hand}</strong> unit{item.quantity_on_hand !== 1 ? 's' : ''} on hand. Deleting it will remove all stock records.
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
